import React from 'react';
import type { TaskFull, MeetingFull } from '@/redux/internal-services/workspaceApiExtensions';
import { monthGrid, isSameDay, dateKey, MEETING_COLORS, PRIORITY_COLOR } from './calendarUtils';

interface Props {
  anchor: Date;
  tasks: TaskFull[];
  meetings: MeetingFull[];
  onDayClick: (d: Date) => void;
  onTaskClick?: (taskId: string) => void;
  onMeetingClick?: (meetingId: string) => void;
}

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MonthView: React.FC<Props> = ({ anchor, tasks, meetings, onDayClick, onTaskClick, onMeetingClick }) => {
  const today = new Date();
  const grid = monthGrid(anchor);
  const curMonth = anchor.getMonth();

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 border-b border-[#EDEDED]">
        {DOW.map((d) => (
          <div key={d} className="py-2 text-center text-[10px] font-semibold text-ui-text3 uppercase tracking-wider">
            {d}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="flex-1 min-h-0 grid grid-rows-6 divide-y divide-[#EDEDED]">
        {grid.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 divide-x divide-[#EDEDED] min-h-0">
            {week.map((day) => {
              const isToday = isSameDay(day, today);
              const isCurrentMonth = day.getMonth() === curMonth;
              const dk = dateKey(day);

              const dayTasks = tasks.filter((t) => t.due_date && dateKey(new Date(t.due_date)) === dk);
              const dayMeetings = meetings.filter((m) => dateKey(new Date(m.start_time)) === dk);
              const total = dayTasks.length + dayMeetings.length;
              const maxVisible = 2;
              const overflow = total - maxVisible;

              // Items to show (meetings first, then tasks)
              const visibleMeetings = dayMeetings.slice(0, maxVisible);
              const remaining = maxVisible - visibleMeetings.length;
              const visibleTasks = dayTasks.slice(0, remaining);

              return (
                <div
                  key={dk}
                  onClick={() => onDayClick(day)}
                  className={`p-1 flex flex-col gap-0.5 cursor-pointer hover:bg-ui-hover transition-colors min-h-0 overflow-hidden ${
                    !isCurrentMonth ? 'opacity-40' : ''
                  }`}
                >
                  {/* Day number */}
                  <div className="flex justify-end mb-0.5">
                    <span className={`text-xs font-semibold w-6 h-6 flex items-center justify-center rounded-full ${
                      isToday
                        ? 'bg-brand-dark text-white'
                        : 'text-ui-text'
                    }`}>
                      {day.getDate()}
                    </span>
                  </div>

                  {/* Meeting chips */}
                  {visibleMeetings.map((m) => {
                    const c = MEETING_COLORS[m.meeting_type] ?? MEETING_COLORS.other;
                    return (
                      <button
                        key={m.meeting_id}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onMeetingClick?.(m.meeting_id); }}
                        className={`w-full text-left text-[9px] font-medium px-1 py-0.5 rounded truncate ${c.bg} ${c.text}`}
                      >
                        {new Date(m.start_time).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} {m.title}
                      </button>
                    );
                  })}

                  {/* Task chips */}
                  {visibleTasks.map((t) => {
                    const dot = PRIORITY_COLOR[t.priority] ?? PRIORITY_COLOR.normal;
                    const isOverdue = new Date(t.due_date!) < today && !['completed', 'cancelled'].includes(t.status);
                    return (
                      <button
                        key={t.task_id}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onTaskClick?.(t.task_id); }}
                        className="w-full text-left text-[9px] font-medium px-1 py-0.5 rounded truncate flex items-center gap-1 bg-ui-subtle border border-[#EDEDED] text-ui-text2 hover:border-brand-dark/30"
                      >
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isOverdue ? 'bg-red-500' : dot}`} />
                        <span className="truncate">{t.title}</span>
                      </button>
                    );
                  })}

                  {/* Overflow */}
                  {overflow > 0 && (
                    <span className="text-[9px] text-ui-text3 font-medium px-1">+{overflow} more</span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MonthView;
